// frontend/src/pages/OKRs.js
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { getOKRs, createOKR, updateOKR, deleteOKR, updateKR, getUsers, getCycles } from '../services/api';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Input, Select,
  SectionTitle, Spinner, ProgressBar
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const STATUS_COLORS = { on_track: 'green', at_risk: 'amber', complete: 'blue', not_started: 'gray' };
const STATUS_LABELS = { on_track: 'On track', at_risk: 'At risk', complete: 'Complete', not_started: 'Not started' };
const KR_COLORS     = { complete: 'var(--navy-500)', in_progress: 'var(--amber-500)', at_risk: 'var(--red-500)', not_started: 'var(--gray-200)' };

export default function OKRs() {
  const { user } = useAuth();
  const { activeDepartmentId } = useActiveDepartment();
  const isAdmin   = user?.role === 'admin';
  const isManager = ['admin','manager','dept_head'].includes(user?.role);

  const [okrs,    setOkrs]    = useState([]);
  const [users,   setUsers]   = useState([]);
  const [cycles,  setCycles]  = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTab,   setActiveTab]   = useState('org');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterCycle, setFilterCycle] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const emptyForm = { title: '', owner_id: '', owner_type: 'user', cycle_id: '', due_date: '', progress: 0, status: 'not_started', key_results: [{ title: '', status: 'not_started' }] };
  const [form, setForm] = useState(emptyForm);

  const loadOkrs = useCallback(async () => {
    const params = { owner_type: activeTab };
    if (activeTab === 'user' && filterOwner) params.user_id = filterOwner;
    if (filterCycle) params.cycle_id = filterCycle;
    const d = await getOKRs(params).catch(() => []);
    setOkrs(Array.isArray(d) ? d : []);
  }, [activeTab, filterOwner, filterCycle]);

  useEffect(() => {
    Promise.all([
      getOKRs({ owner_type: 'org' }).catch(() => []),
      getUsers().catch(() => []),
      getCycles().catch(() => []),
    ]).then(([o, u, c]) => {
      setOkrs(Array.isArray(o) ? o : []);
      setUsers(Array.isArray(u) ? u : []);
      const cyc = Array.isArray(c) ? c : [];
      setCycles(cyc);
      const active = cyc.find(x => x.status === 'active');
      if (active) setFilterCycle(String(active.id));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (!loading) loadOkrs(); }, [activeTab, filterOwner, filterCycle, loadOkrs, loading]);

  const openCreate = () => {
    const ownerType = activeTab === 'org' ? 'org' : 'user';
    setEditing(null);
    // Org goals are aligned to the department currently in view (blank = org-wide).
    setForm({
      ...emptyForm, owner_type: ownerType, owner_id: String(user?.id || ''), cycle_id: filterCycle,
      department_id: ownerType === 'org' && activeDepartmentId ? String(activeDepartmentId) : '',
    });
    setShowForm(true);
  };

  const openEdit = o => {
    setEditing(o);
    setForm({ ...o, owner_id: String(o.owner_id), owner_type: o.owner_type || 'user', cycle_id: String(o.cycle_id || ''), key_results: o.key_results?.length ? o.key_results : [{ title: '', status: 'not_started' }] });
    setShowForm(true);
  };

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await updateOKR(editing.id, form);
      else await createOKR(form);
      await loadOkrs(); setShowForm(false);
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Archive this objective?')) return;
    await deleteOKR(id); await loadOkrs();
  };

  const handleKRStatus = async (krId, status) => {
    await updateKR(krId, { status }); await loadOkrs();
  };

  const addKR    = () => setForm(f => ({ ...f, key_results: [...f.key_results, { title: '', status: 'not_started' }] }));
  const removeKR = i => setForm(f => ({ ...f, key_results: f.key_results.filter((_, idx) => idx !== i) }));
  const setKR    = (i, val) => setForm(f => ({ ...f, key_results: f.key_results.map((k, idx) => idx === i ? { ...k, ...val } : k) }));

  const canCreate = activeTab === 'org' ? isAdmin : isManager;
  const canEdit   = activeTab === 'org' ? isAdmin : isManager;

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="okrs" helpTask="set-okrs" />
        {canCreate && <Btn size="sm" onClick={openCreate}>+ New {activeTab === 'org' ? 'org goal' : 'objective'}</Btn>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '0.5px solid var(--border)', marginBottom: 18 }}>
        {[['org','Org goals'],['user','Employee OKRs']].map(([id,label]) => (
          <button key={id} onClick={() => { setActiveTab(id); setFilterOwner(''); }}
            style={{ padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 13, fontWeight: activeTab === id ? 500 : 400,
              color: activeTab === id ? 'var(--navy-600)' : 'var(--text-3)',
              borderBottom: `2px solid ${activeTab === id ? 'var(--navy-600)' : 'transparent'}`, marginBottom: -1 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <Select value={filterCycle} onChange={e => setFilterCycle(e.target.value)} style={{ width: 220 }}>
          <option value="">All cycles</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        {activeTab === 'user' && isManager && (
          <Select value={filterOwner} onChange={e => setFilterOwner(e.target.value)} style={{ width: 200 }}>
            <option value="">All employees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        )}
      </div>

      {/* OKR cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {okrs.map(obj => (
          <Card key={obj.id}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                {activeTab === 'org'
                  ? <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--navy-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🎯</div>
                  : <Avatar initials={obj.avatar_initials} size={32} />
                }
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{obj.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    {activeTab === 'org' ? 'Organisation' : obj.owner_name}
                    {obj.due_date ? ` · Due ${new Date(obj.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                <span style={{ fontSize: 20, fontWeight: 500, color: obj.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)' }}>{obj.progress}%</span>
                <Badge color={STATUS_COLORS[obj.status]}>{STATUS_LABELS[obj.status]}</Badge>
                {canEdit && (
                  <>
                    <Btn variant="ghost" size="sm" onClick={() => openEdit(obj)}>Edit</Btn>
                    <Btn variant="ghost" size="sm" onClick={() => handleDelete(obj.id)} style={{ color: 'var(--red-500)' }}>✕</Btn>
                  </>
                )}
              </div>
            </div>

            <ProgressBar value={obj.progress} color={obj.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)'} height={6} />

            {obj.key_results?.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {obj.key_results.map(kr => (
                  <div key={kr.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: KR_COLORS[kr.status], flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-2)', flex: 1 }}>{kr.title}</span>
                    {canEdit && (
                      <select value={kr.status} onChange={e => handleKRStatus(kr.id, e.target.value)}
                        style={{ fontSize: 11, padding: '2px 6px', border: '0.5px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontFamily: 'inherit', cursor: 'pointer' }}>
                        <option value="not_started">Not started</option>
                        <option value="in_progress">In progress</option>
                        <option value="complete">Complete</option>
                        <option value="at_risk">At risk</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
        {okrs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)', fontSize: 13 }}>
            {activeTab === 'org'
              ? 'No org goals for this cycle. Admins can create organisation-wide goals.'
              : 'No employee objectives found. Create one to get started.'}
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit objective' : activeTab === 'org' ? 'New org goal' : 'New objective'}>
        <form onSubmit={handleSubmit}>
          <Field label={activeTab === 'org' ? 'Org goal title' : 'Objective title'} required>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={activeTab === 'org' ? 'e.g. Achieve 95% platform uptime' : 'e.g. Ship v2 energy analytics platform'} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'user' && isManager ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>
            {activeTab === 'user' && isManager && (
              <Field label="Owner">
                <Select value={form.owner_id} onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))}>
                  <option value="">Select owner...</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
              </Field>
            )}
            <Field label="Review cycle">
              <Select value={form.cycle_id} onChange={e => setForm(f => ({ ...f, cycle_id: e.target.value }))}>
                <option value="">No cycle</option>
                {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Status">
              <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="not_started">Not started</option>
                <option value="on_track">On track</option>
                <option value="at_risk">At risk</option>
                <option value="complete">Complete</option>
              </Select>
            </Field>
            <Field label={`Progress: ${form.progress}%`}>
              <input type="range" min="0" max="100" step="5" value={form.progress}
                onChange={e => setForm(f => ({ ...f, progress: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: 'var(--navy-500)', marginTop: 8 }} />
            </Field>
          </div>

          <SectionTitle>Key results</SectionTitle>
          {form.key_results.map((kr, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <Input value={kr.title} onChange={e => setKR(i, { title: e.target.value })} placeholder={`Key result ${i + 1}`} />
              <Select value={kr.status} onChange={e => setKR(i, { status: e.target.value })} style={{ width: 130 }}>
                <option value="not_started">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="complete">Complete</option>
                <option value="at_risk">At risk</option>
              </Select>
              {form.key_results.length > 1 && (
                <Btn type="button" variant="ghost" size="sm" onClick={() => removeKR(i)} style={{ color: 'var(--red-500)', flexShrink: 0 }}>✕</Btn>
              )}
            </div>
          ))}
          <Btn type="button" variant="ghost" size="sm" onClick={addKR} style={{ marginBottom: 16 }}>+ Add key result</Btn>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Save changes' : 'Create'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
